/**
 * Транзиентный рисунок: то, что видно, пока ведут мышь.
 *
 * ЧЕМ ЭТО НЕ ЯВЛЯЕТСЯ. Не изменением сцены. Версия не растёт,
 * в журнал операций и в очередь записи не попадает ничего, отменить
 * это нечем и незачем: жест заканчивается настоящей операцией,
 * она и запишется. Тот же род сообщения, что `live`, и живёт оно
 * так же — поверх состояния, а не в нём.
 *
 * ЧЕМ ОТЛИЧАЕТСЯ ОТ `live`. Там слой уже есть и двигается; здесь
 * слоя нет вовсе — он появится, когда руку отпустят. Поэтому
 * и ключом служит не слой, а тот, кто рисует: рисующих может быть
 * несколько сразу.
 *
 * Разбор общий для панели и оверлея по той же причине, что у `live`:
 * вторая реализация того же разошлась бы с первой молча.
 */
/** Виды, которые умеет нарисовать виджет фигуры. */
import { decodeMessage } from './message.js';
const KINDS = new Set(['rect', 'ellipse', 'line', 'arrow', 'stroke']);
/**
 * Потолок на число точек.
 *
 * Совпадает с потолком виджета (@owm/widgets, MAX_POINTS) и hub'а.
 * Совпадать он обязан: штрих, который пропустят там и отвергнут
 * здесь, виден только зрителю и только в эфире.
 */
export const MAX_SKETCH_POINTS = 4000;
/**
 * Сколько живёт штрих без продолжения.
 *
 * Больше срока живого перетаскивания: точки штриха идут кадрами,
 * но между двумя движениями руки человек замирает, целясь.
 * Пропавший на полсекунды штрих читался бы как поломка.
 *
 * И заведомо меньше того, что терпимо смотреть на чужой рисунок,
 * которого уже нет: оборвись связь у рисующего — забудется сам.
 *
 * СРОК ОБЩИЙ ДЛЯ ОБОИХ КОНЦОВ, и потому живёт здесь, рядом
 * с разбором, — тот же довод, что у LIVE_TTL_MS. Отправитель обязан
 * знать, сколько его молчание терпят (SKETCH_KEEPALIVE_MS).
 */
export const SKETCH_TTL_MS = 3_000;
/**
 * Через сколько молчания повторить последний штрих.
 *
 * ЗАМЕРЕТЬ ПОСРЕДИ ЛИНИИ — ОБЫЧНОЕ ДЕЛО: обводят деталь в кадре,
 * примеряются, ждут, пока договорит собеседник. Кадры штриха в это
 * время не идут — рука стоит, — и смотрящий, дождавшись срока,
 * забывал незаконченный рисунок совсем. Продолжение после этого
 * выглядело как второй штрих, начатый из ниоткуда.
 *
 * Втрое чаще срока, как и у перетаскивания: одно потерянное
 * сообщение — обычное дело, и запас должен оставлять место второй
 * попытке.
 */
export const SKETCH_KEEPALIVE_MS = Math.floor(SKETCH_TTL_MS / 3);
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
/**
 * Разбирает сообщение `sketch`.
 *
 * Возвращает `null`, если сообщение не о рисунке ИЛИ если оно
 * негодное. Второе важно не меньше первого: транзиент нигде
 * не сохранён, и убрать нарисованный мусор было бы нечем —
 * только переподключением оверлея.
 */
export function parseSketch(raw) {
    const message = decodeMessage(raw);
    if (message === null || message.t !== 'sketch')
        return null;
    const sketch = decodeMessage(message.sketch);
    if (sketch === null)
        return null;
    const clientId = sketch.clientId;
    if (typeof clientId !== 'string' || clientId === '')
        return null;
    const kind = sketch.kind;
    if (typeof kind !== 'string' || !KINDS.has(kind))
        return null;
    const color = sketch.color;
    if (typeof color !== 'string' || !HEX_COLOR.test(color))
        return null;
    const width = finite(sketch.width);
    if (width === null || width < 0)
        return null;
    const points = sketch.points;
    if (!Array.isArray(points))
        return null;
    // Координаты идут парами x, y. Нечётная длина означает потерянную
    // половину точки: рисунок вышел бы не тем, что рисуют
    if (points.length < 4 || points.length % 2 !== 0)
        return null;
    if (points.length > MAX_SKETCH_POINTS * 2)
        return null;
    for (const value of points) {
        if (finite(value) === null)
            return null;
    }
    return { clientId, kind, points: points, color, width };
}
/** Число, которое можно нарисовать: не NaN и не бесконечность. */
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
//# sourceMappingURL=sketch.js.map